import React from 'react'
import { ActionType, getType, createAction } from 'typesafe-actions'
import * as E from 'fp-ts/lib/Either'
import axios, { AxiosError } from 'axios'
import { useContextHelper } from 'shared/hooks/useContextHelper'
import { notification } from 'shared/notification'
import storage from 'auth/Storage'
import config from 'shared/config'
import {
    AuthResponse,
    AuthResponseCodec,
    MaestroRole,
    MaestroUserData,
    MaestroUserDataCodec,
    MyIDUser,
} from '@genome-web-forms/common/auth'
import BackendPrimer from 'shared/BackendPrimer'
import { isAxiosError } from '@genome-web-forms/common/error'
import qs from 'query-string'

import isString from 'lodash/isString'
import history from 'routing/history'
import { History } from 'history'
import { getSnowplowUser, setUserId } from 'shared/util/snowplowUtils'
import { AxiosRequestConfigExtended } from '@genome-web-forms/common/api'

const debug = require('debug')('gwf.auth') // eslint-disable-line

interface AuthenticationPending {
    authenticationPending: true
    signedIn: null
    user: null
}

interface NotSignedIn {
    authenticationPending: false
    signedIn: false
    user: null
}

interface SignedIn {
    authenticationPending: false
    signedIn: true
    user: MyIDUser
}

type AuthValueActual = AuthenticationPending | NotSignedIn | SignedIn

// pretend user always exists for the benefit of context users
export type AuthValue =
    | {
          authenticationPending: true
          signedIn: undefined
          user: MyIDUser
      }
    | {
          authenticationPending: false
          signedIn: false
          user: MyIDUser
      }
    | {
          authenticationPending: false
          signedIn: true
          user: MyIDUser
      }

type ParseUserType = {
    maybeUser: E.Either<any, MaestroUserData>
    authResponse: AuthResponse
}

export const actions = {
    setAuthenticationPending: createAction('AUTH/SET_AUTHENTICATION_PENDING')(),
    signin: createAction('AUTH/SIGNIN')<{
        user: MyIDUser
    }>(),
    signout: createAction('AUTH/SIGNOUT')(),
}

const reducer = (_: AuthValueActual, action: ActionType<typeof actions>): AuthValueActual => {
    switch (action.type) {
        case getType(actions.setAuthenticationPending): {
            return {
                authenticationPending: true,
                signedIn: null,
                user: null,
            }
        }

        case getType(actions.signin): {
            const { user } = action.payload

            BackendPrimer.prime(user)

            const snowplowUser = getSnowplowUser(user)

            setUserId(snowplowUser.email)

            return {
                authenticationPending: false,
                signedIn: true,
                user,
            }
        }

        case getType(actions.signout): {
            storage.clearAuthValue()

            return {
                authenticationPending: false,
                signedIn: false,
                user: null,
            }
        }
    }
}

export const AuthContext = React.createContext<AuthValue>(null as any)
AuthContext.displayName = 'Auth'
export const AuthDispatchContext = React.createContext<React.Dispatch<ActionType<typeof actions>>>(
    null as any,
)
AuthDispatchContext.displayName = 'AuthDispatch'
export function useAuthContext(): AuthValue {
    return useContextHelper(AuthContext, 'AuthContext')
}
export function useUser(): MyIDUser {
    const { user } = useAuthContext()
    if (!user) {
        throw new Error(
            `MyIDUser not available. You're probably using "useUser()" in a context where unauthenticated users have access.`,
        )
    }

    return user
}

export const queryToHashSwap = (): void => {
    const search = history.location.search
    if (search && window.location.pathname === '/') {
        history.replace({
            ...history.location,
            hash: search.substring(1),
            search: '',
        })
    }
}

type MyIDHashResponse = E.Either<
    { error: string; error_description?: string },
    { code?: string; state?: string }
>

export function consumeMyIDHash(history: History): MyIDHashResponse {
    const hash = history.location.hash

    const parsed = qs.parse(hash)
    debug('parsed hash', parsed)

    const { error, error_description, code, state } = parsed
    if (isString(error)) {
        return E.left({
            error,
            error_description: error_description as string | undefined,
        })
    }

    history.replace({
        ...history.location,
        hash: '',
    })

    if (!isString(code) || !isString(state)) {
        return E.right({})
    }

    return E.right({ code, state })
}

declare global {
    // eslint-disable-next-line no-var
    var __EARLY_AUTH_FETCH: Promise<unknown> | undefined
}

type AuthProps = {
    history?: History
}

const buildHeadersForUser = (userData: MaestroUserData, authData: AuthResponse): MyIDUser => {
    // Normalize data so it is compatible with PartialUser that is used by API
    const udata: any = { ...userData }
    // Normalization related to migration to ACME. ACME user data has different props then MyID user data

    udata.roles = ((udata.roles as MaestroRole[]) || []).map((r: any) => {
        if (!r.attributes) {
            r.attributes = []
        }
        if (!r.functionalAbilities) {
            r.functionalAbilities = []
        }
        return r
    })

    // if(udata.rolesFullAssertion) {
    //     delete udata.rolesFullAssertion
    // }

    const res = {
        ...(udata as any),
        'x-gsso-myid': authData.access_token,
        'x-gsso-myid-refresh-token': authData.refresh_token,
        id_token: authData.id_token,
    }

    return res
}

type RefreshTokenState = {
    isRefreshing: boolean
    refreshingCall: null | Promise<any>
    authValue: AuthValueActual
    gotFreshAuthData: boolean
    timer: ReturnType<typeof setTimeout> | null
}

const refreshToken = (token: string, state: RefreshTokenState): Promise<any> => {
    if (state.isRefreshing) {
        return state.refreshingCall as Promise<any>
    }

    state.isRefreshing = true
    const refreshingCall = fetch(
        `${config.cpAuthHost}/as/token.oauth2?` +
            new URLSearchParams({
                grant_type: 'refresh_token',
            }).toString(),
        {
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + token,
            },
        },
    )
        .then(r => {
            if (r.ok) {
                return r.json()
            }
            throw new Error(`Status: ${r.status || 'Unknown'}. Refresh request failed`)
        })
        .then((authData: AuthResponse) => {
            const authValue = state.authValue as SignedIn
            const user = authValue.user
            // Assign new auth headers
            user.id_token = authData.id_token
            user['x-gsso-myid-refresh-token'] = authData.refresh_token
            user['x-gsso-myid'] = authData.access_token
            storage.setAuthValue(authValue)
            state.isRefreshing = false
            state.refreshingCall = null
            state.gotFreshAuthData = true
            !!state.timer && clearTimeout(state.timer)
            state.timer = setTimeout(() => {
                state.gotFreshAuthData = false
            }, AUTH_DATA_AGE)
            return Promise.resolve(true)
        })
        .catch(e => {
            state.gotFreshAuthData = false
            !!state.timer && clearTimeout(state.timer)
            state.timer = null
            throw e
        })
    state.refreshingCall = refreshingCall
    return refreshingCall
}

const buildRequestConfigFromError = (
    requestConfig: AxiosRequestConfigExtended,
    refreshingState: RefreshTokenState,
): AxiosRequestConfigExtended => {
    // Expected to have a fresh reset state and new id_token
    const accessToken = refreshingState.authValue.user?.id_token
    let headers = requestConfig.headers
    headers = { ...headers, Authorization: 'Bearer ' + accessToken }
    const newConfig: AxiosRequestConfigExtended = {
        ...requestConfig,
        headers: headers,
        unauthorized: true,
    }
    return newConfig
}

// 8 hours by default
const AUTH_DATA_AGE = 1 * 60 * 60 * 1000 // 1 hour

export const useRefreshToken = (
    authValue: AuthValueActual,
    dispatch: React.Dispatch<any>,
): void => {
    React.useEffect(() => {
        // Make sure to execute interceptors once after login
        if (!authValue.signedIn) {
            return
        }

        const refreshingState: RefreshTokenState = {
            isRefreshing: false,
            refreshingCall: null,
            authValue: authValue,
            gotFreshAuthData: false,
            timer: null,
        }

        axios.interceptors.response.use(
            async response => {
                return response
            },
            async (error: AxiosError<any>) => {
                if ((error?.config as AxiosRequestConfigExtended)?.unauthorized) {
                    // If the first attempt to request data with fresh auth data fails
                    // the http error will propagate further
                    return Promise.reject(error)
                }

                const status = error.response ? error.response.status : null

                const recentRefreshToken = authValue.user['x-gsso-myid-refresh-token']
                if (status === 403 || status === 401) {
                    if (refreshingState.gotFreshAuthData) {
                        const reqConfig = buildRequestConfigFromError(error.config, refreshingState)
                        return axios.request(reqConfig)
                    }

                    return refreshToken(recentRefreshToken, refreshingState)
                        .then(_ => {
                            const reqConfig = buildRequestConfigFromError(
                                error.config,
                                refreshingState,
                            )
                            return axios.request(reqConfig)
                        })
                        .catch(() => {
                            dispatch(actions.signout())
                        })
                }

                return Promise.reject(error)
            },
        )
    }, [authValue, dispatch])
}

const Auth: React.FC<AuthProps> = ({ children, history: outerHistory }): React.ReactElement => {
    const [authValue, dispatch] = React.useReducer(reducer, {
        authenticationPending: true,
        signedIn: null,
        user: null,
    })

    React.useEffect(() => {
        try {
            const v = storage.getAuthValue()
            if (v?.signedIn) {
                return dispatch(actions.signin(v as SignedIn))
            }
        } catch (e) {}

        const accessDataEither = consumeMyIDHash(outerHistory ?? history)
        debug('accessData', accessDataEither)

        if (E.isLeft(accessDataEither)) {
            // const { error, error_description } = accessDataEither.left

            notification.error(
                <>
                    <strong>MyID authentication failed!</strong>
                    <br />
                    <br />
                    {/* <strong>{error}:</strong> {error_description} */}
                    <span>Internal MyID error. Please try again.</span>
                </>,
            )

            return dispatch(actions.signout())
        }

        const code = accessDataEither.right.code || ''
        const state = accessDataEither.right.state || ''

        const earlyFetch = window.__EARLY_AUTH_FETCH

        if (!code || !state) {
            dispatch(actions.signout())
            return
        }

        const handleAuthResponse = (authRespPromise: Promise<any>) => {
            return authRespPromise
                .then(authData => {
                    const authDataDecoded = AuthResponseCodec.decode(authData)

                    if (E.isRight(authDataDecoded)) {
                        history.replace(decodeURIComponent(authDataDecoded.right.return_to))
                        const maybeUser = MaestroUserDataCodec.decode(
                            JSON.parse(atob(authDataDecoded.right.user_data)),
                        )
                        return {
                            maybeUser,
                            authResponse: authDataDecoded.right,
                        }
                    } else {
                        throw new Error('invalid auth data')
                    }
                })
                .then(parseUser)
                .catch(() => {
                    dispatch(actions.signout())
                })
        }

        // Non Local Environments
        if (earlyFetch) {
            window.__EARLY_AUTH_FETCH = undefined
            handleAuthResponse(earlyFetch)
            return void 0
        }

        // Local dev environment
        const devFetch = fetch(
            `${config.discoveryServiceUrl}/auth/cwr/callback?` +
                new URLSearchParams({
                    code: code,
                    state: state,
                }).toString(),
            {},
        ).then(r => r.json())

        handleAuthResponse(devFetch)

        let stopped = false
        return () => {
            stopped = true
        }
        ///////////////////////////////

        function parseUser(parseData: ParseUserType): void {
            const maybeUser = parseData.maybeUser
            const authData = parseData.authResponse

            if (stopped) {
                return
            }

            if (E.isRight(maybeUser)) {
                const user = buildHeadersForUser(maybeUser.right, authData)
                // TODO: Remove this after roles related logic is settled/tested
                // user.roles = [
                //     { name: 'Access Dev', functionalAbilities: [], attributes: [] },
                //     { name: 'Access Prod', functionalAbilities: [], attributes: [] },
                //     { name: 'Access QA', functionalAbilities: [], attributes: [] },
                //     { name: 'Access Staging', functionalAbilities: [], attributes: [] },
                //     {
                //         name: 'GWF Read Only',
                //         functionalAbilities: [
                //             { securedEntities: [], name: 'READ', attributes: [] },
                //         ],
                //         attributes: [],
                //     },
                //     { name: 'Library Read', functionalAbilities: [], attributes: [] },
                //     { name: 'Library Write', functionalAbilities: [], attributes: [] },
                //     {
                //         name: 'DEI',
                //         functionalAbilities: [
                //             { securedEntities: [], name: 'WRITE DEI', attributes: [] },
                //         ],
                //         attributes: [],
                //     },
                //     {
                //         name: 'GWF Tag+Publish',
                //         functionalAbilities: [
                //             { securedEntities: [], name: 'WRITE', attributes: [] },
                //             { securedEntities: [], name: 'READ', attributes: [] },
                //             { securedEntities: [], name: 'PUBLISH', attributes: [] },
                //         ],
                //         attributes: [],
                //     },
                // ] as any

                return dispatch(actions.signin({ user }))
            }

            const error = maybeUser.left

            if (isAxiosError(error)) {
                dispatch(actions.signout())
                return void 0
            }

            throw error
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Saving this data to avoid auth call for each page load since "code" is expiring after 5 minutes
    React.useEffect(() => {
        try {
            storage.setAuthValue(authValue)
        } catch (e) {}
    }, [authValue])

    useRefreshToken(authValue, dispatch)

    return (
        <AuthContext.Provider value={authValue as AuthValue}>
            <AuthDispatchContext.Provider value={dispatch}>{children}</AuthDispatchContext.Provider>
        </AuthContext.Provider>
    )
}

export default Auth
